import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import InstallPWAPrompt from "./components/InstallPWAPrompt";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import Attendance from "./pages/Attendance";
import Leaves from "./pages/Leaves";
import Payroll from "./pages/Payroll";
import Recruitment from "./pages/Recruitment";
import Performance from "./pages/Performance";
import AIPerformance from "./pages/AIPerformance";
import Onboarding from "./pages/Onboarding";
import Documents from "./pages/Documents";
import Reports from "./pages/Reports";
import OrgChart from "./pages/OrgChart";
import Settings from "./pages/Settings";
import Announcements from "./pages/Announcements";
import AdvancesLoans from "./pages/AdvancesLoans";
import HRTasks from "./pages/HRTasks";
import Appraisals from "./pages/Appraisals";
import Analytics from "./pages/Analytics";
import LeavePolicy from "./pages/LeavePolicy";
import Assets from "./pages/Assets";
import ExitManagement from "./pages/ExitManagement";
import FnFSettlement from "./pages/FnFSettlement";
import MyDashboard from "./pages/self-service/MyDashboard";
import MyProfile from "./pages/self-service/MyProfile";
import MyLeaves from "./pages/self-service/MyLeaves";
import MyPayslips from "./pages/self-service/MyPayslips";
import MyAttendance from "./pages/self-service/MyAttendance";
import MyOnboarding from "./pages/self-service/MyOnboarding";
import MyDocuments from "./pages/self-service/MyDocuments";
import MyAnnouncements from "./pages/self-service/MyAnnouncements";
import MyAdvances from "./pages/self-service/MyAdvances";
import MyPerformance from "./pages/self-service/MyPerformance";
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === "employee" ? "/my-dashboard" : "/dashboard"} replace />;
  }
  
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user?.role === "employee" ? "/my-dashboard" : "/dashboard"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<HomeRedirect />} />
            {/* Admin routes */}
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="employees" element={<Employees />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="recruitment" element={<Recruitment />} />
            <Route path="performance" element={<Performance />} />
            <Route path="ai-performance" element={<AIPerformance />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="documents" element={<Documents />} />
            <Route path="org-chart" element={<OrgChart />} />
            <Route path="reports" element={<Reports />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="advances-loans" element={<AdvancesLoans />} />
            <Route path="hr-tasks" element={<HRTasks />} />
            <Route path="appraisals" element={<Appraisals />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="leave-policy" element={<LeavePolicy />} />
            <Route path="assets" element={<Assets />} />
            <Route path="exit-management" element={<ExitManagement />} />
            <Route path="fnf-settlement" element={<FnFSettlement />} />
            <Route path="settings" element={<Settings />} />
            {/* Self-service routes */}
            <Route path="my-dashboard" element={<MyDashboard />} />
            <Route path="my-profile" element={<MyProfile />} />
            <Route path="my-leaves" element={<MyLeaves />} />
            <Route path="my-payslips" element={<MyPayslips />} />
            <Route path="my-attendance" element={<MyAttendance />} />
            <Route path="my-onboarding" element={<MyOnboarding />} />
            <Route path="my-documents" element={<MyDocuments />} />
            <Route path="my-announcements" element={<MyAnnouncements />} />
            <Route path="my-advances" element={<MyAdvances />} />
            <Route path="my-performance" element={<MyPerformance />} />
          </Route>
        </Routes>
        <Toaster position="top-right" richColors />
        <InstallPWAPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
